import React, { useEffect, useState } from 'react';
import { Alert, Box, LinearProgress, Pagination, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { apiError } from '../../Documents/format';
import { RUN_ITEM_COLORS, RUN_ITEM_LABELS } from '../constants';
import { isCancelled, timesheetApi } from '../timesheetApi';
import { RunItemResult, RunItemsPage } from '../types';

interface Props {
  runId: number;
}

type Filter = RunItemResult | '';

const FILTERS: Array<{ value: Filter; label: string }> = [
  { value: '', label: 'Все' },
  { value: 'created', label: 'Созданные' },
  { value: 'exists', label: 'Уже были' },
  { value: 'failed', label: 'Ошибки' }
];

/** Результат одного запуска по аптекам: создан / уже был / ошибка, с фильтром и страницами по 100 */
const RunItems: React.FC<Props> = ({ runId }) => {
  const [filter, setFilter] = useState<Filter>('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<RunItemsPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    timesheetApi.runItems(runId, filter, page, controller.signal)
      .then(res => { setData(res); setError(null); })
      .catch(err => { if (!isCancelled(err)) setError(apiError(err, 'Не удалось загрузить результат запуска')); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [runId, filter, page]);

  const items = data ? data.items : [];
  const pagination = data ? data.pagination : { total: 0, page: 1, limit: 100 };
  const pages = Math.max(1, Math.ceil(pagination.total / pagination.limit));

  return (
    <Box sx={{ p: 1.5, bgcolor: '#f8fafc' }}>
      <ToggleButtonGroup
        size="small" exclusive value={filter} sx={{ mb: 1 }}
        onChange={(_, value: Filter | null) => { if (value !== null) { setFilter(value); setPage(1); } }}
      >
        {FILTERS.map(f => (
          <ToggleButton key={f.value || 'all'} value={f.value} sx={{ textTransform: 'none', px: 1.5, py: 0.25, fontSize: 12.5 }}>{f.label}</ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Box sx={{ height: 3 }}>{loading && <LinearProgress sx={{ height: 3 }} />}</Box>
      {error && <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>}

      <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
        {items.map(item => (
          <Box key={`${item.pharmacyNum}-${item.createdAt}`} sx={{ display: 'flex', gap: 1.5, py: 0.35, fontSize: 13 }}>
            <Typography component="span" sx={{ width: 100, flexShrink: 0, fontSize: 13, fontWeight: 600 }}>Аптека №{item.pharmacyNum}</Typography>
            <Typography component="span" sx={{ width: 70, flexShrink: 0, fontSize: 13, color: RUN_ITEM_COLORS[item.result] }}>
              {RUN_ITEM_LABELS[item.result]}
            </Typography>
            <Typography component="span" sx={{ fontSize: 13, color: item.result === 'failed' ? '#b91c1c' : '#64748b' }}>
              {item.result === 'failed' ? item.error || '-' : item.fileName || '-'}
            </Typography>
          </Box>
        ))}
        {!loading && !error && items.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>Нет записей</Typography>
        )}
      </Box>

      {pages > 1 && (
        <Pagination size="small" count={pages} page={page} onChange={(_, p) => setPage(p)} sx={{ mt: 1 }} />
      )}
    </Box>
  );
};

export default RunItems;
